import type { JSX } from 'react';
import { LinkOutlined, TeamOutlined } from '@ant-design/icons';

import { Panel, Tag } from '../ds';
import type { UkResultDto } from '../../lib/api/openingTasks';
import { statusBadge, ukResultViews } from './stage-tasks';

/**
 * «От управляющей компании» (владелец 19.10.2026): то, что УК сделала для точки и прислала ссылкой, — например,
 * «Создать сообщество ВК — vk.com/club… · Доступ выдан ✓ / ждём». Берётся из задач стороны «УК» карточки, где отмечен
 * пункт-«ссылка»; остальные пункты той же задачи показывают, что уже сделано, а что ещё ждём. Нет результатов — блока нет.
 */
export function UkResultsPanel({
  results,
  sub = 'то, что УК подготовила для вашей точки',
}: {
  results: readonly UkResultDto[] | undefined;
  /** Подпись блока: менеджеру УК — «для этой точки». */
  sub?: string;
}): JSX.Element | null {
  const views = ukResultViews(results);
  if (views.length === 0) return null;
  return (
    <Panel pad={0} icon={<TeamOutlined />} title="От управляющей компании" sub={sub}>
      <div className="fk-ot-uk">
        {views.map((v) => {
          const badge = statusBadge(v.status);
          return (
            <div key={v.id} className="fk-ot-uk-row">
              <div className="fk-ot-uk-title">
                <span>{v.title}</span>
                {badge ? <Tag color={badge.tone}>{badge.text}</Tag> : null}
              </div>
              <div className="fk-ot-uk-links">
                {v.links.map((l, i) => (
                  <span key={`${i}-${l.href}`}>
                    <LinkOutlined aria-hidden="true" />{' '}
                    <a href={l.href} target="_blank" rel="noopener noreferrer" title={l.href}>
                      {l.label}
                    </a>
                    {l.title ? <span className="fk-ot-muted"> · {l.title}</span> : null}
                  </span>
                ))}
              </div>
              {v.checks.length > 0 ? (
                <div className="fk-ot-uk-checks">
                  {v.checks.map((c, i) => (
                    <span key={`${i}-${c.title}`} data-done={c.done}>
                      {c.text}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
